// src/components/ProductReviews.js

import React, { useEffect, useState } from 'react';
import { getReviews, submitReview } from '../lib/reviewsApi';
import { NEW_RELEASE_HANDLES } from '../data/scents';
import StarRating from './StarRating';

const inputClass = 'w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground text-sm focus:outline-none focus:border-[#CEAC8F] transition-colors';

// Shown on a new release's page until its first review is published, then
// replaced by the normal reviews list automatically.
const FirstReviewForm = ({ productId, productName }) => {
    const [form, setForm] = useState({ rating: 0, name: '', email: '', title: '', body: '', website: '' });
    const [status, setStatus] = useState('idle'); // idle | sending | sent
    const [error, setError] = useState(null);
    const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.rating) {
            setError('Please choose a star rating.');
            return;
        }
        setStatus('sending');
        setError(null);
        const result = await submitReview({ productId, ...form });
        if (result.ok) {
            setStatus('sent');
        } else {
            setStatus('idle');
            setError(result.error);
        }
    };

    if (status === 'sent') {
        return (
            <div className="bg-card rounded-2xl border border-[#CEAC8F]/40 p-8 text-center">
                <p className="text-foreground text-lg font-light mb-2">Thank you for your review.</p>
                <p className="text-muted-foreground text-sm">It will appear here once it's been approved.</p>
            </div>
        );
    }

    return (
        <div className="bg-card rounded-2xl border border-border p-6 sm:p-8">
            <p className="text-[#CEAC8F] text-xs tracking-widest uppercase mb-2">New Release</p>
            <h3 className="text-foreground text-2xl font-light mb-2">Be the first to review {productName}</h3>
            <p className="text-muted-foreground text-sm mb-6">
                Tried it already? Tell everyone what you think.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <p className="text-foreground text-sm mb-2">Your rating</p>
                    <StarRating rating={form.rating} onChange={(rating) => setForm({ ...form, rating })} size={28} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input type="text" required maxLength={80} placeholder="Your name" value={form.name} onChange={update('name')} className={inputClass} aria-label="Your name" />
                    <input type="email" required maxLength={200} placeholder="Your email (not shown)" value={form.email} onChange={update('email')} className={inputClass} aria-label="Your email" />
                </div>
                <input type="text" maxLength={150} placeholder="Review title (optional)" value={form.title} onChange={update('title')} className={inputClass} aria-label="Review title" />
                <textarea required maxLength={5000} rows={4} placeholder="What did you think?" value={form.body} onChange={update('body')} className={inputClass} aria-label="Your review" />
                {/* Honeypot: hidden from people, filled in by bots */}
                <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update('website')} className="hidden" aria-hidden="true" />

                {error && <p className="text-red-400 text-sm">{error}</p>}

                <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="w-full sm:w-auto px-8 py-3 rounded-lg bg-[#CEAC8F] text-black text-sm font-medium hover:bg-[#d4b896] transition-colors disabled:opacity-60"
                >
                    {status === 'sending' ? 'Sending…' : 'Submit Review'}
                </button>
            </form>
        </div>
    );
};

const ProductReviews = ({ productId, handle, productName }) => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!productId) return;
        setLoading(true);
        getReviews({ productId, limit: 50 })
            .then(setReviews)
            .catch((err) => console.error('Failed to load reviews:', err))
            .finally(() => setLoading(false));
    }, [productId]);

    if (loading) {
        return (
            <div className="py-12 text-center text-muted-foreground animate-pulse">Loading reviews...</div>
        );
    }

    const safeReviews = Array.isArray(reviews) ? reviews : [];
    const total = safeReviews.length;
    const average = total ? safeReviews.reduce((sum, r) => sum + r.rating, 0) / total : 0;
    const breakdown = [5, 4, 3, 2, 1].map((star) => ({
        star,
        count: safeReviews.filter((r) => r.rating === star).length,
    }));

    return (
        <section className="py-16 border-t border-border">
            <h2 className="text-3xl font-light text-foreground tracking-wide mb-8">Reviews</h2>

            {total === 0 && NEW_RELEASE_HANDLES.includes(handle) ? (
                <FirstReviewForm productId={productId} productName={productName} />
            ) : total === 0 ? (
                <p className="text-muted-foreground">No reviews yet — be the first!</p>
            ) : (
                <>
                    <div className="flex flex-col sm:flex-row gap-10 mb-12">
                        <div className="flex flex-col items-center sm:items-start">
                            <p className="text-5xl font-light text-foreground mb-2">{average.toFixed(1)}</p>
                            <StarRating rating={Math.round(average)} size={20} />
                            <p className="text-muted-foreground text-sm mt-2">
                                {total} review{total !== 1 ? 's' : ''}
                            </p>
                        </div>
                        <div className="flex-1 space-y-1.5 max-w-sm">
                            {breakdown.map(({ star, count }) => (
                                <div key={star} className="flex items-center gap-3 text-sm">
                                    <span className="text-muted-foreground w-10">{star} star</span>
                                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                        <div
                                            className="h-full bg-[#CEAC8F]"
                                            style={{ width: total ? `${(count / total) * 100}%` : '0%' }}
                                        />
                                    </div>
                                    <span className="text-muted-foreground w-6 text-right">{count}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-8">
                        {safeReviews.map((review, i) => (
                            <div key={i} className="border-b border-border pb-8 last:border-b-0">
                                <div className="flex items-center justify-between mb-2">
                                    <StarRating rating={review.rating} size={16} />
                                    <span className="text-muted-foreground text-xs">
                                        {new Date(review.created_at).toLocaleDateString()}
                                    </span>
                                </div>
                                {review.title && (
                                    <h3 className="text-foreground font-medium mb-1">{review.title}</h3>
                                )}
                                <p className="text-muted-foreground leading-relaxed mb-3">{review.body}</p>
                                {review.photo_urls?.length > 0 && (
                                    <div className="flex gap-2 mb-3">
                                        {review.photo_urls.map((url, idx) => (
                                            <img
                                                key={idx}
                                                src={url}
                                                alt=""
                                                className="w-20 h-20 rounded-lg object-cover border border-border"
                                            />
                                        ))}
                                    </div>
                                )}
                                <p className="text-foreground text-sm font-medium">{review.customer_name || 'Anonymous'}</p>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </section>
    );
};

export default ProductReviews;
